import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const GoogleMap = () => {
  const mapRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  // RUDT Bau Adresse
  const location = {
    address: 'Wassenbergerstr. 41, 52525 Heinsberg',
    lat: 51.0723,
    lng: 6.1199,
    phone: '02452 860 70 19'
  };

  useEffect(() => {
    // API Key aus Umgebungsvariablen
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      setError('Google Maps API Key fehlt');
      console.error('VITE_GOOGLE_MAPS_API_KEY ist nicht in .env definiert');
      return;
    }

    // Prüfen ob Google Maps bereits geladen ist
    if (window.google && window.google.maps) {
      initMap();
      return;
    }

    // Google Maps Script laden
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setIsLoaded(true);
      initMap();
    };
    script.onerror = () => {
      setError('Google Maps konnte nicht geladen werden');
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup beim Unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const initMap = () => {
    if (!mapRef.current || !window.google) return;

    try {
      // Map erstellen
      const map = new window.google.maps.Map(mapRef.current, {
        center: { lat: location.lat, lng: location.lng },
        zoom: 15,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
        zoomControl: true,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'on' }]
          }
        ]
      });

      // Custom Marker Icon (RUDT Bau Logo)
      const customIcon = {
        url: 'https://canv.wasacon.com/rudt-bau/_assets/media/be98d041c9972c824096f49dc08c4e70.png',
        scaledSize: new window.google.maps.Size(60, 60),
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(30, 60)
      };

      // Marker mit Logo erstellen
      const marker = new window.google.maps.Marker({
        position: { lat: location.lat, lng: location.lng },
        map: map,
        title: 'RUDT Bau',
        icon: customIcon,
        animation: window.google.maps.Animation.DROP
      });

      // Info Window mit zentriertem Logo, Adresse, Telefon und Route-Button
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 20px; font-family: 'Inter', sans-serif; text-align: center; min-width: 220px;">
            <div style="display: flex; justify-content: center; align-items: center; margin-bottom: 16px;">
              <img 
                src="https://canv.wasacon.com/rudt-bau/_assets/media/be98d041c9972c824096f49dc08c4e70.png" 
                alt="RUDT Bau Logo" 
                style="width: 100px; height: 100px; object-fit: contain;"
              />
            </div>
            <div style="margin-bottom: 12px; color: #374151; line-height: 1.5;">
              <div style="font-weight: 600; font-size: 14px; margin-bottom: 4px;">Wassenbergerstr. 41</div>
              <div style="font-size: 14px;">52525 Heinsberg</div>
            </div>
            <div style="margin-bottom: 12px;">
              <a 
                href="tel:+4924528607019" 
                style="color: #3466ad; text-decoration: none; font-weight: 500; font-size: 14px; display: inline-block; padding: 4px 0;"
              >
                📞 02452 860 70 19
              </a>
            </div>
            <a 
              href="https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lng}" 
              target="_blank"
              rel="noopener noreferrer"
              style="display: inline-block; background-color: #3466ad; color: white; padding: 10px 20px; border-radius: 8px; text-decoration: none; font-weight: 600; font-size: 14px; margin-top: 4px; transition: background-color 0.2s;"
              onmouseover="this.style.backgroundColor='#2a5291'"
              onmouseout="this.style.backgroundColor='#3466ad'"
            >
              🗺️ Route berechnen
            </a>
          </div>
        `
      });

      // Info Window bei Klick auf Marker öffnen
      marker.addListener('click', () => {
        infoWindow.open(map, marker);
      });

      // Info Window standardmäßig öffnen
      infoWindow.open(map, marker);

    } catch (err) {
      console.error('Fehler beim Initialisieren der Karte:', err);
      setError('Karte konnte nicht initialisiert werden');
    }
  };

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="bg-red-50 border border-red-200 h-[600px] rounded-xl flex items-center justify-center"
      >
        <div className="text-center px-4">
          <p className="text-red-600 text-lg font-medium mb-2">Fehler beim Laden der Karte</p>
          <p className="text-red-500 text-sm">{error}</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: 0.2 }}
      className="relative h-[600px] rounded-xl overflow-hidden shadow-2xl"
    >
      <div 
        ref={mapRef} 
        className="w-full h-full"
        style={{ minHeight: '600px' }}
      />
      
      {!isLoaded && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#3466ad] mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Karte wird geladen...</p>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default GoogleMap;